import { useState } from 'react';

import { track } from '@/config/observability';
import { useAuth, type AuthContextType } from '@/contexts/AuthContext';
import { classifyAuthError, type AuthErrorAction } from '@/lib/auth/auth-error';
import {
  AuthMode,
  LoginForm,
  RegisterForm,
  ResetPasswordForm,
} from '@/lib/schemas/auth';

export type AuthFormValues = LoginForm | RegisterForm | ResetPasswordForm;

/** The context calls a submit needs, plus the page callback. */
type SubmitDeps = Pick<
  AuthContextType,
  'signIn' | 'signUp' | 'resetPassword'
> & {
  onStateChange: (state: string, email?: string) => void;
};

/**
 * Run the request for a mode and return the raw error, or null.
 *
 * Module scope rather than a closure inside the hook purely to keep the hook
 * itself readable - nothing here touches component state, and the success
 * transitions it does make go through `onStateChange`.
 */
async function runSubmit(
  mode: AuthMode,
  values: AuthFormValues,
  deps: SubmitDeps
): Promise<unknown> {
  if (mode === 'login') {
    const { email, password } = values as LoginForm;
    const loginError = (await deps.signIn(email, password)).error ?? null;
    if (!loginError) track('logged_in', { method: 'password' });
    return loginError;
  }

  if (mode === 'register') {
    const { email, password, fullName } = values as RegisterForm;
    const result = await deps.signUp(email, password, fullName);
    if (result.error) return result.error;
    // Branch on the session, not merely on the absence of an error. With
    // Supabase's "Confirm email" switched OFF, signUp returns a live session
    // and the user is already signed in - telling them to go and click a link
    // that will never arrive is what the old code did. With it ON there is no
    // session, and the confirm screen is correct.
    const session = (result.data as { session?: unknown } | null | undefined)
      ?.session;
    track('signed_up', { method: 'password', needs_confirmation: !session });
    deps.onStateChange(session ? 'signed-in' : 'registration-success', email);
    return null;
  }

  const result = await deps.resetPassword(values.email);
  if (result.error) return result.error;
  deps.onStateChange('reset-email-sent', values.email);
  return null;
}

/**
 * Submit + magic-link behaviour for `AuthForm`, extracted so the component is
 * markup and this is the decisions.
 *
 * Two separate pending flags, deliberately. The old form gated both the submit
 * and the magic-link button on a single `isLoading`, so pressing "Sign in" put
 * the magic-link button into a busy state it had no reason to be in and it read
 * as though both requests were in flight.
 *
 * The submit helpers return the raw error rather than a message, so there is
 * exactly one place - `applyError` - that decides what a failure means. That
 * used to be spread across each helper, which is how everything except the
 * banned case ended up rendering Supabase's own sentence with no way out. See
 * `lib/auth/auth-error`.
 */
export function useAuthSubmit({
  mode,
  onStateChange,
}: {
  mode: AuthMode;
  onStateChange: (state: string, email?: string) => void;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMagicLinkPending, setIsMagicLinkPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorAction, setErrorAction] = useState<AuthErrorAction | null>(null);
  const [bannedEmail, setBannedEmail] = useState<string | null>(null);
  const { signIn, signUp, resetPassword, signInWithOtp } = useAuth();

  /** Route a failure to the screen that can act on it. */
  const applyError = (err: unknown, email: string) => {
    const classified = classifyAuthError(err, mode);

    if (classified.kind === 'banned') {
      setBannedEmail(email);
      return;
    }
    // Signed up but never clicked the link. `AuthSuccess` owns this state
    // because it owns the resend button; showing the message inline here would
    // be the dead end this whole classifier exists to remove.
    if (classified.kind === 'unconfirmed') {
      onStateChange('email-unconfirmed', email);
      return;
    }

    setError(classified.message);
    setErrorAction(classified.action ?? null);
  };

  const onSubmit = async (values: AuthFormValues) => {
    setIsSubmitting(true);
    setError(null);
    setErrorAction(null);
    setBannedEmail(null);

    const err = await runSubmit(mode, values, {
      signIn,
      signUp,
      resetPassword,
      onStateChange,
    });

    if (err) applyError(err, values.email);
    setIsSubmitting(false);
  };

  const requestMagicLink = async (email: string) => {
    if (!email) {
      // Not a Supabase failure - nothing was sent - so it carries no action.
      setError('Enter your email address first, then request the link.');
      setErrorAction(null);
      return;
    }
    setIsMagicLinkPending(true);
    setError(null);
    setErrorAction(null);
    const { error: otpError } = await signInWithOtp(email);
    if (otpError) applyError(otpError, email);
    else onStateChange('magic-link-sent', email);
    setIsMagicLinkPending(false);
  };

  return {
    isSubmitting,
    isMagicLinkPending,
    error,
    errorAction,
    bannedEmail,
    setBannedEmail,
    onSubmit,
    requestMagicLink,
  };
}
